import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useFetch } from "../../hooks/useFetch";
import { fetchRoomService } from "../../services/modelsService";
import { transferService } from "../../services/tokenService";
import { useAuthContext } from "../../context/AuthProvider";
import { STORAGE } from "../../utils/headers";
import { genderLabel } from "../../utils/labels";
import { Modal, Button, Form } from 'react-bootstrap';
import Loader from "../../components/shared/Loader";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { useRoomChat } from "../../hooks/useRoomChat";
import { usePlayback } from "../../hooks/usePlayback";
import RoomChat from "../../components/components-streaming/RoomChat";
import noimg from "../../assets/img/noimg.jpg";
// El reproductor nativo de MediaMTX se maneja en un iframe, pero la URL ya no
// se construye con el streaming_token: la emite el backend firmada con un
// ticket de vida corta que MediaMTX valida en cada lectura.

import "./live.css";

export default function App() {
  const { id } = useParams();

  useDocumentMeta({ noIndex: true });
  const { data: model, error: roomError } = useFetch(fetchRoomService, { params: { id } });

  const { user, balance, refreshBalance, updateAccess } = useAuthContext();
  const navigate = useNavigate();

  // Chat público, hilos privados, avisos y presencia de la sala.
  const chat = useRoomChat(model, user);

  // Ticket firmado de reproducción, renovado automáticamente mientras dure la sala.
  const {
    playbackUrl,
    error: playbackError,
    loading: playbackLoading,
    isLive,
    isOffline,
    retry: retryPlayback,
  } = usePlayback(model?.id, { enabled: Boolean(model) });
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [loadingTransfer, setLoadingTransfer] = useState(false);
  const [transferStatus, setTransferStatus] = useState({ type: '', message: '' });

  // 402 = recarga vencida o sin saldo: se sincroniza el estado y se envía a la
  // pantalla de recarga explicando el motivo.
  useEffect(() => {
    if (playbackError?.status === 402) {
      updateAccess(playbackError.access);
      navigate('/recharge', {
        replace: true,
        state: { from: `/live/${id}`, reason: playbackError.access?.denial_reason },
      });
    }
  }, [playbackError, id, navigate, updateAccess]);

  const handleSendTip = async (e) => {
    e.preventDefault();
    if (!tipAmount || tipAmount <= 0) return;
    if (balance < tipAmount) {
      setTransferStatus({ type: 'error', message: 'Saldo insuficiente' });
      return;
    }

    setLoadingTransfer(true);
    setTransferStatus({ type: '', message: '' });

    try {
      // `room_id` hace que el backend anuncie la propina en el chat público:
      // así toda la sala se entera de quién dio tokens, y no solo quien pagó.
      await transferService({
        to_user_id: model?.user_id,
        amount: parseInt(tipAmount),
        room_id: model?.id
      }, user.token);

      setTransferStatus({ type: 'success', message: `¡Has enviado ${tipAmount} tokens!` });
      setTipAmount('');
      refreshBalance();

      setTimeout(() => {
        setShowTipModal(false);
        setTransferStatus({ type: '', message: '' });
      }, 2000);

    } catch (error) {
      setTransferStatus({ type: 'error', message: error.message || 'Error al enviar tokens' });
    } finally {
      setLoadingTransfer(false);
    }
  };

  // Un fallo al abrir la sala tiene que decirse: antes el servicio devolvía
  // `null` en silencio y la pantalla se quedaba con "Cargando..." para siempre.
  if (roomError) {
    return (
      <div className="live-stream-container">
        <div className="player-message">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          <p>{roomError}</p>
          <Link to="/live" className="action-btn btn-secondary-custom mt-3">
            Volver a las salas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="live-stream-container">
      {/* Sección de Video */}
      <div className="video-player-section">
        <div className="player-wrapper-outer">
          <div className="video-overlay-top">
            <div className="model-info-banner">
              {/* Sin foto el `src` quedaba vacío y el navegador pintaba el
                  icono de imagen rota sobre el vídeo. */}
              <img
                src={model?.img ? `${STORAGE}/${model.img}` : noimg}
                onError={(e) => { e.target.onerror = null; e.target.src = noimg; }}
                alt=""
                className="model-banner-avatar"
                width="36"
                height="36"
              />
              <h5 className="model-banner-name">{model?.name || 'Cargando...'}</h5>

              {/* El cartel refleja la señal, no el campo `status` de la modelo:
                  ese es una foto del momento en que se cargó la página y
                  anunciaba "EN VIVO" con la emisión ya caída. */}
              {isLive ? (
                <span className="live-badge" style={{ padding: '2px 8px', fontSize: '0.7rem' }}>
                  <span className="pulse-dot"></span> EN VIVO
                </span>
              ) : isOffline ? (
                <span className="live-badge status-offline" style={{ padding: '2px 8px', fontSize: '0.7rem' }}>
                  FUERA DE LÍNEA
                </span>
              ) : (
                <span className="live-badge status-busy" style={{ padding: '2px 8px', fontSize: '0.7rem' }}>
                  CONECTANDO
                </span>
              )}
            </div>
            {/* Aforo real: sale del canal de presencia de la sala, no de un
                número inventado a partir del id de la modelo. */}
            <div className={`viewer-count ${chat.connected ? '' : 'is-stale'}`}
                 title={chat.connected
                   ? `${chat.viewerCount} ${chat.viewerCount === 1 ? 'persona viendo' : 'personas viendo'}`
                   : 'Sin conexión en vivo: el aforo puede estar desactualizado'}>
              <i className="fa-solid fa-eye me-1"></i> {chat.viewerCount}
            </div>
          </div>

          <div className="ratio ratio-16x9 h-100 position-relative" style={{ backgroundColor: '#000', borderRadius: '12px', overflow: 'hidden' }}>
            {playbackUrl ? (
              <iframe
                src={playbackUrl}
                width="100%"
                height="100%"
                scrolling="no"
                allowFullScreen
                allow="autoplay; encrypted-media; picture-in-picture"
                title="MediaMTX Stream"
                style={{ border: 'none' }}
              />
            ) : playbackError && playbackError.status !== 402 ? (
              <div className="player-message">
                <i className={`fa-solid ${isOffline ? 'fa-moon' : 'fa-video-slash'}`}></i>
                <p>{playbackError.message}</p>
                {isOffline && (
                  <small className="text-muted">
                    La sala se abrirá sola en cuanto empiece la transmisión.
                  </small>
                )}
                <button className="action-btn btn-secondary-custom" onClick={retryPlayback}>
                  <i className="fa-solid fa-rotate-right"></i> Reintentar ahora
                </button>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center h-100 text-white gap-2">
                <div className="spinner-border text-afrodita-gold" role="status"></div>
                {playbackLoading && <small className="text-muted">Autorizando reproducción...</small>}
              </div>
            )}
          </div>
        </div>

        <div className="stream-details-area">
          <div className="stream-identity">
            <div className="stream-identity-avatar">{model?.name?.[0]?.toUpperCase()}</div>
            <div className="stream-identity-text">
              <h4 className="stream-identity-name">{model?.name}</h4>
              {/* Decía "Modelo Elite • Verificada" fijo para todo el mundo: una
                  distinción que el sistema no otorga ni comprueba. Se enseña lo
                  que de verdad hay en su perfil. */}
              <p className="stream-identity-meta">
                {model?.tagline
                  || [genderLabel(model?.gender), model?.age && `${model.age} años`, model?.city]
                       .filter(Boolean).join(' • ')
                  || 'En directo'}
              </p>
            </div>
          </div>

          <div className="stream-action-btns">
            {model?.slug && (
              <Link to={`/modelo/${model.slug}`} className="action-btn btn-secondary-custom">
                <i className="fa-solid fa-id-badge" aria-hidden="true"></i> Ver perfil
              </Link>
            )}
            {/* Había un botón "Seguir" sin `onClick`: pulsarlo no hacía nada y
                no existe nada detrás que lo sostenga. Un control muerto
                confunde más que su ausencia. */}
            <button
              className="action-btn btn-secondary-custom"
              onClick={() => chat.showTab('private')}
            >
              <i className="fa-solid fa-lock" aria-hidden="true"></i> Privado
            </button>
            <button
              className="action-btn btn-tip"
              onClick={() => {
                if (!user) {
                  alert('Debes iniciar sesión para enviar propinas');
                  return;
                }
                setShowTipModal(true);
              }}
            >
              <i className="fa-solid fa-coins"></i> Enviar Propinas
            </button>
          </div>
        </div>
      </div>

      <RoomChat chat={chat} user={user} />

      <Modal show={showTipModal} onHide={() => setShowTipModal(false)} centered className="tip-modal">
        <Modal.Header closeButton className="bg-dark text-white border-secondary">
          <Modal.Title>Enviar propina a {model?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-white">
          <Loader loading={loadingTransfer} message="Enviando tokens..." />

          <div className="text-center mb-4">
            <div className="balance-info mb-3">
              <span className="text-muted">Tu Saldo: </span>
              <span className="text-afrodita-gold fw-bold">{balance || 0} Tokens</span>
            </div>

            <div className="tip-options d-flex flex-wrap justify-content-center gap-2 mb-4">
              {[5, 10, 20, 50, 100].map(amt => (
                <button
                  key={amt}
                  className={`btn ${tipAmount === amt.toString() ? 'btn-afrodita-gold' : 'btn-outline-gold'}`}
                  onClick={() => setTipAmount(amt.toString())}
                >
                  {amt}
                </button>
              ))}
            </div>

            <Form onSubmit={handleSendTip}>
              <Form.Group className="mb-3">
                <Form.Label>Cantidad Personalizada</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="bg-transparent text-white border-secondary text-center fs-4"
                  style={{ color: 'gold' }}
                />
              </Form.Group>

              {transferStatus.message && (
                <div className={`alert ${transferStatus.type === 'success' ? 'alert-success' : 'alert-danger'} py-2 small`}>
                  {transferStatus.message}
                </div>
              )}

              <Button
                variant="primary"
                type="submit"
                className="w-100 btn-tip-send py-2 fw-bold"
                disabled={!tipAmount || tipAmount <= 0 || loadingTransfer}
              >
                ENVIAR TOKENS
              </Button>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
